import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import { BookingFormData } from '../types/api';
import { Appointment } from '../types/appointment';
import { parseTimeStringLA, APP_TIMEZONE, formatDateLA, formatTimeLA } from '../utils/timezone';
import { availabilityService } from './availabilityService';
import { BOOKING_CONFIGS } from '../types/booking';

class DirectBookingService {
  /**
   * Submit appointment directly to Firestore (bypassing Cloud Functions)
   * This is useful for debugging and testing
   */
  async createAppointmentDirectly(formData: BookingFormData): Promise<{ success: boolean; appointmentId?: string; error?: string }> {
    try {
      console.log('Creating appointment directly in Firestore:', formData);

      // Validate required fields
      if (!formData.firstName?.trim()) {
        throw new Error('First name is required');
      }

      if (!formData.selectedDate || !formData.selectedTime) {
        throw new Error('Date and time are required');
      }

      // Check if the time slot is still available (both disabled slots and existing appointments)
      const isSlotAvailable = await availabilityService.isTimeSlotAvailable(formData.selectedDate, formData.selectedTime);
      if (!isSlotAvailable) {
        throw new Error('The selected time slot is no longer available. Please choose a different time.');
      }

      // Combine date and time
      const scheduledDateTime = this.combineDateTime(formData.selectedDate, formData.selectedTime);

      // Create appointment document with both UTC timestamp and human-readable LA time
      const appointment: Omit<Appointment, 'id'> & {
        scheduledDate?: string;
        scheduledTime?: string;
        scheduledDateTimeLA?: string;
      } = {
        customerInfo: {
          firstName: formData.firstName.trim(),
          email: formData.email?.trim() || '',
          ...(formData.phone && { phone: formData.phone.trim() }),
          ...(formData.wechatId && { wechatId: formData.wechatId.trim() }),
          message: formData.message?.trim() || '',
          uploadedFiles: formData.uploadedFiles || [],
        },
        serviceType: formData.selectedService,
        scheduledDateTime: Timestamp.fromDate(scheduledDateTime),
        scheduledDate: formatDateLA(scheduledDateTime), // e.g., "2025-12-29"
        scheduledTime: formData.selectedTime, // e.g., "10:00 AM" - exactly what user selected
        scheduledDateTimeLA: `${formatDateLA(scheduledDateTime)} ${formData.selectedTime}`, // e.g., "2025-12-29 10:00 AM"
        timezone: APP_TIMEZONE, // Always use America/Los_Angeles timezone
        duration: formData.duration || 15, // Use provided duration or default to 15 minutes
        
        // Payment Information (for paid appointments)
        ...(formData.checkoutId && { 
          checkoutId: formData.checkoutId,
          paymentStatus: 'paid' // If checkoutId is provided, payment was successful
        }),
        
        preferences: {
          addToCalendar: formData.addToCalendar,
          smsReminder: formData.smsReminder,
          communicationChannel: formData.meetingMethod, // Use the selected meeting method
        },
        status: 'pending',
        notifications: {
          confirmationEmailSent: false,
          calendarInviteSent: false,
          reminderScheduled: false,
          smsReminderSent: false,
        },
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      console.log('📝 About to write to Firestore:');
      console.log('  scheduledDateTime (Timestamp/UTC):', scheduledDateTime.toISOString());
      console.log('  scheduledDate (LA):', appointment.scheduledDate);
      console.log('  scheduledTime (LA):', appointment.scheduledTime);
      console.log('  scheduledDateTimeLA (readable):', appointment.scheduledDateTimeLA);
      console.log('  timezone:', appointment.timezone);

      // Write directly to Firestore
      const docRef = await addDoc(collection(db, 'appointments'), appointment);
      
      console.log('Appointment created successfully with ID:', docRef.id);

      // Also add this time slot to disabled slots to prevent double booking
      try {
        await this.addDisabledSlot(formData.selectedDate, formData.selectedTime, docRef.id);
        console.log('Added disabled slot for appointment:', docRef.id);
      } catch (error) {
        console.warn('Failed to add disabled slot, but appointment was created:', error);
        // Don't fail the entire booking if disabled slot creation fails
      }

      return {
        success: true,
        appointmentId: docRef.id,
      };

    } catch (error: any) {
      console.error('Direct booking error:', error);
      return {
        success: false,
        error: error.message || 'Failed to create appointment',
      };
    }
  }

  /**
   * Combine date and time into a Date object (LA timezone)
   */
  private combineDateTime(date: Date, time: string): Date {
    console.group('🕐 DirectBooking: Combining Date & Time');
    console.log('Input date:', date.toDateString());
    console.log('Input time:', time);
    
    // Parse the time in LA timezone and combine with date
    const combinedDate = parseTimeStringLA(date, time);
    
    console.log('Combined result (toString):', combinedDate.toString());
    console.log('Combined result (ISO/UTC):', combinedDate.toISOString());
    console.log('Display in LA timezone:', combinedDate.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', timeZoneName: 'short' }));
    console.log('Display in NY timezone:', combinedDate.toLocaleString('en-US', { timeZone: 'America/New_York', timeZoneName: 'short' }));
    console.log('UTC Hours:', combinedDate.getUTCHours());
    console.groupEnd();
    
    return combinedDate;
  }

  /**
   * Add a disabled slot for a booked appointment
   * This prevents double booking by marking the slot as unavailable
   */
  private async addDisabledSlot(date: Date, time: string, appointmentId: string): Promise<void> {
    const dateStr = formatDateLA(date);
    
    const disabledSlot = {
      date: dateStr,
      time: time,
      reason: 'booked',
      appointmentId: appointmentId,
      createdAt: Timestamp.now(),
    };

    await addDoc(collection(db, 'disabledSlots'), disabledSlot);
  }
}

export const directBookingService = new DirectBookingService();
export default directBookingService;
